import {
    BellIcon,
    ChartBarIcon,
    CheckCircleIcon,
    ClockIcon,
    CloudArrowUpIcon,
    CpuChipIcon,
    DocumentIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    MapPinIcon,
    PhotoIcon,
    ShieldCheckIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalDocuments: 0,
    verifiedDocuments: 0,
    pendingDocuments: 0,
    suspiciousActivities: 0,
    aiGeneratedDetected: 0,
    emailsSent: 0,
    flaggedLocations: 0,
    detectionAccuracy: 0
  });
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiDetections, setAiDetections] = useState([]);
  const [emailNotifications, setEmailNotifications] = useState([]);
  const [flaggedUploads, setFlaggedUploads] = useState([]);
  const [detectionTrends, setDetectionTrends] = useState([]);
  const [realtimeThreats, setRealtimeThreats] = useState([]);
  const [threatCount, setThreatCount] = useState(0);

  useEffect(() => {
    // Add dummy data for demonstration
    setStats({
      totalDocuments: 1247,
      verifiedDocuments: 1098,
      pendingDocuments: 89,
      suspiciousActivities: 60,
      aiGeneratedDetected: 143,
      emailsSent: 218,
      flaggedLocations: 37,
      detectionAccuracy: 98.6
    });

    setRecentDocuments([
      {
        id: 1,
        filename: "passport_john_doe.pdf",
        status: "verified",
        created_at: "2024-10-14T10:30:00Z",
        confidence: 92.5
      },
      {
        id: 2,
        filename: "id_card_jane_smith.jpg",
        status: "pending",
        created_at: "2024-10-14T09:15:00Z",
        confidence: null
      },
      {
        id: 3,
        filename: "drivers_license_mike.pdf",
        status: "flagged",
        created_at: "2024-10-14T08:45:00Z",
        confidence: 23.1
      },
      {
        id: 4,
        filename: "birth_certificate_alice.jpg",
        status: "verified",
        created_at: "2024-10-14T07:20:00Z",
        confidence: 87.3
      },
      {
        id: 5,
        filename: "proof_of_payment_bob.pdf",
        status: "verified",
        created_at: "2024-10-14T06:50:00Z",
        confidence: 95.8
      }
    ]);

    setActivities([
      {
        id: 1,
        activity_type: "High-risk document detected",
        location: "Harare, Zimbabwe",
        timestamp: "2024-10-14T10:45:00Z",
        severity: "high"
      },
      {
        id: 2,
        activity_type: "Police alert triggered",
        location: "Bulawayo, Zimbabwe", 
        timestamp: "2024-10-14T10:30:00Z",
        severity: "critical"
      },
      {
        id: 3,
        activity_type: "Document verification successful",
        location: "Mutare, Zimbabwe",
        timestamp: "2024-10-14T10:15:00Z",
        severity: "low"
      },
      {
        id: 4,
        activity_type: "Suspicious pattern identified",
        location: "Gweru, Zimbabwe",
        timestamp: "2024-10-14T09:50:00Z",
        severity: "medium"
      },
      {
        id: 5,
        activity_type: "Geo-location anomaly",
        location: "Masvingo, Zimbabwe",
        timestamp: "2024-10-14T09:30:00Z",
        severity: "medium"
      }
    ]);

    // AI Detection Analytics
    setAiDetections([
      {
        id: 1,
        filename: "profile_image_456.jpg",
        aiConfidence: 97.8,
        generatorType: "Stable Diffusion",
        location: "Harare Central, Zimbabwe",
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        notificationSent: true
      },
      {
        id: 2,
        filename: "id_photo_789.png",
        aiConfidence: 94.2,
        generatorType: "DALL-E",
        location: "Bulawayo, Zimbabwe",
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        notificationSent: true
      },
      {
        id: 3,
        filename: "passport_photo_321.jpg",
        aiConfidence: 91.5,
        generatorType: "Midjourney",
        location: "Mutare, Zimbabwe",
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
        notificationSent: true
      },
      {
        id: 4,
        filename: "document_scan_654.png",
        aiConfidence: 89.3,
        generatorType: "GAN-based",
        location: "Gweru, Zimbabwe",
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        notificationSent: false
      }
    ]);

    // Email Notifications
    setEmailNotifications([
      {
        id: 1,
        recipient: "security@police.gov.zw",
        subject: "ALERT: AI-Generated Image Detected - High Risk",
        status: "delivered",
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        location: "Harare Central"
      },
      {
        id: 2,
        recipient: "admin@cybersecurity.gov.zw",
        subject: "ALERT: Synthetic Image Upload from Bulawayo",
        status: "delivered",
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        location: "Bulawayo"
      },
      {
        id: 3,
        recipient: "security@police.gov.zw",
        subject: "ALERT: Deepfake Detection - Mutare Region",
        status: "delivered",
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
        location: "Mutare"
      },
      {
        id: 4,
        recipient: "alerts@national-security.gov.zw",
        subject: "ALERT: GAN-Generated Content Flagged",
        status: "pending",
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        location: "Gweru"
      }
    ]);

    // Flagged Upload Locations
    setFlaggedUploads([
      { location: "Harare Central", count: 28, lat: -17.8252, lng: 31.0335 },
      { location: "Bulawayo", count: 15, lat: -20.1393, lng: 28.5833 },
      { location: "Mutare", count: 12, lat: -18.9707, lng: 32.6707 },
      { location: "Gweru", count: 9, lat: -19.4500, lng: 29.8167 },
      { location: "Chitungwiza", count: 7, lat: -18.0000, lng: 31.1000 }
    ]);

    // Detection Trends (Last 7 Days)
    setDetectionTrends([
      { day: "Mon", aiDetected: 18, legitimate: 142 },
      { day: "Tue", aiDetected: 23, legitimate: 156 },
      { day: "Wed", aiDetected: 15, legitimate: 168 },
      { day: "Thu", aiDetected: 31, legitimate: 145 },
      { day: "Fri", aiDetected: 27, legitimate: 159 },
      { day: "Sat", aiDetected: 19, legitimate: 128 },
      { day: "Sun", aiDetected: 10, legitimate: 98 }
    ]);

    setLoading(false);
    // Uncomment to fetch real data when backend is ready
    // fetchDashboardData();
  }, []);

  // Real-time threat simulation
  useEffect(() => {
    const locations = [
      { city: "Harare Central", country: "Zimbabwe", lat: -17.8252, lng: 31.0335 },
      { city: "Bulawayo", country: "Zimbabwe", lat: -20.1393, lng: 28.5833 },
      { city: "Mutare", country: "Zimbabwe", lat: -18.9707, lng: 32.6707 },
      { city: "Gweru", country: "Zimbabwe", lat: -19.4500, lng: 29.8167 },
      { city: "Chitungwiza", country: "Zimbabwe", lat: -18.0000, lng: 31.1000 },
      { city: "Masvingo", country: "Zimbabwe", lat: -20.0638, lng: 30.8277 },
      { city: "Kadoma", country: "Zimbabwe", lat: -18.3333, lng: 29.9167 },
      { city: "Kwekwe", country: "Zimbabwe", lat: -18.9167, lng: 29.8167 }
    ];

    const threatTypes = [
      { type: "AI-Generated Image", severity: "critical", color: "red" },
      { type: "Deepfake Detection", severity: "high", color: "orange" },
      { type: "Synthetic ID Upload", severity: "critical", color: "red" },
      { type: "GAN-based Forgery", severity: "high", color: "orange" },
      { type: "Manipulated Document", severity: "medium", color: "yellow" },
      { type: "Face Swap Detected", severity: "critical", color: "red" }
    ];

    const interval = setInterval(() => {
      const randomLocation = locations[Math.floor(Math.random() * locations.length)];
      const randomThreat = threatTypes[Math.floor(Math.random() * threatTypes.length)];
      
      const newThreat = {
        id: Date.now() + Math.random(),
        location: randomLocation.city,
        country: randomLocation.country,
        lat: randomLocation.lat,
        lng: randomLocation.lng,
        threatType: randomThreat.type,
        severity: randomThreat.severity,
        color: randomThreat.color,
        timestamp: new Date().toISOString(),
        confidence: (85 + Math.random() * 14).toFixed(1)
      };

      setRealtimeThreats(prev => {
        const updated = [newThreat, ...prev];
        return updated.slice(0, 15); // Keep only last 15 threats
      });

      setThreatCount(prev => prev + 1);

      // Update stats dynamically
      setStats(prev => ({
        ...prev,
        aiGeneratedDetected: prev.aiGeneratedDetected + 1,
        suspiciousActivities: prev.suspiciousActivities + 1
      }));
    }, 3000); // New threat every 3 seconds

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch statistics
      const statsResponse = await fetch('/api/documents/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      // Fetch recent documents
      const docsResponse = await fetch('/api/documents?limit=5&sort=created_at_desc', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (docsResponse.ok) {
        const docsData = await docsResponse.json();
        setRecentDocuments(docsData.documents || []);
      }

      // Fetch recent activities
      const activitiesResponse = await fetch('/api/map/activities?limit=10', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (activitiesResponse.ok) {
        const activitiesData = await activitiesResponse.json();
        setActivities(activitiesData.activities || []);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircleIcon className="h-5 w-5 text-green-400" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-400" />;
      case 'flagged':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 'flagged':
        return 'bg-red-500/20 text-red-400 border border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-green-400 font-mono">
            <span className="text-blue-400">&gt;</span> Welcome back, {user?.username || 'User'}!
          </h1>
          <p className="mt-2 text-gray-300 font-mono">
            <span className="text-green-500">#</span> Monitor your document verification activities and system security.
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-green-400/50 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentIcon className="h-8 w-8 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-mono text-gray-400 truncate">
                    Total Documents
                  </dt>
                  <dd className="text-lg font-mono text-green-400">
                    {stats.totalDocuments.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-green-400/50 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-mono text-gray-400 truncate">
                    Verified
                  </dt>
                  <dd className="text-lg font-mono text-green-400">
                    {stats.verifiedDocuments.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-green-400/50 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-mono text-gray-400 truncate">
                    Pending
                  </dt>
                  <dd className="text-lg font-mono text-yellow-400">
                    {stats.pendingDocuments}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-red-400/50 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-mono text-gray-400 truncate">
                    Suspicious
                  </dt>
                  <dd className="text-lg font-mono text-red-400">
                    {stats.suspiciousActivities}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Real-Time Threat Monitor */}
        <div className="mb-8 bg-gradient-to-br from-black via-gray-900 to-black border border-red-500/30 rounded-lg shadow-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-red-500/30 bg-gradient-to-r from-red-900/20 to-orange-900/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-400 animate-pulse" />
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                </div>
                <h2 className="text-lg font-mono text-red-400 font-bold">
                  <span className="text-red-500">[</span>LIVE THREAT MONITOR<span className="text-red-500">]</span>
                </h2>
                <span className="px-3 py-1 rounded-full text-xs font-mono bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse">
                  ACTIVE
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-sm font-mono text-gray-400">
                  <span className="text-red-400">Total Threats:</span> {threatCount}
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                  <span className="text-xs font-mono text-red-400">SCANNING</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-6 bg-black/50 backdrop-blur-sm">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Threat Feed */}
              <div className="lg:col-span-2">
                <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
                  {realtimeThreats.length === 0 ? (
                    <div className="text-center py-12">
                      <ExclamationTriangleIcon className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-500 font-mono text-sm">Monitoring for threats...</p>
                    </div>
                  ) : (
                    realtimeThreats.map((threat, index) => (
                      <div
                        key={threat.id}
                        className={`border rounded-lg p-3 transition-all duration-500 ${
                          threat.severity === 'critical'
                            ? 'border-red-500/50 bg-red-500/10 animate-pulse-slow'
                            : threat.severity === 'high'
                            ? 'border-orange-500/50 bg-orange-500/10'
                            : 'border-yellow-500/50 bg-yellow-500/10'
                        } ${index === 0 ? 'animate-slide-in-left scale-105' : ''}`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className="relative mt-1">
                              <MapPinIcon className={`h-5 w-5 ${
                                threat.color === 'red' ? 'text-red-400' :
                                threat.color === 'orange' ? 'text-orange-400' :
                                'text-yellow-400'
                              }`} />
                              {index < 3 && (
                                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold ${
                                  threat.severity === 'critical'
                                    ? 'bg-red-500/30 text-red-300 border border-red-500/50'
                                    : threat.severity === 'high'
                                    ? 'bg-orange-500/30 text-orange-300 border border-orange-500/50'
                                    : 'bg-yellow-500/30 text-yellow-300 border border-yellow-500/50'
                                }`}>
                                  {threat.severity.toUpperCase()}
                                </span>
                                <span className="text-xs font-mono text-gray-300 font-semibold">
                                  {threat.threatType}
                                </span>
                              </div>
                              <div className="space-y-0.5">
                                <p className="text-xs font-mono text-gray-400">
                                  <span className="text-blue-400">📍 Location:</span> {threat.location}, {threat.country}
                                </p>
                                <p className="text-xs font-mono text-gray-400">
                                  <span className="text-purple-400">🎯 Coordinates:</span> {threat.lat.toFixed(4)}, {threat.lng.toFixed(4)}
                                </p>
                                <p className="text-xs font-mono text-gray-400">
                                  <span className="text-green-400">🔍 Confidence:</span> {threat.confidence}%
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="text-right ml-3">
                            <div className="text-xs font-mono text-gray-500 whitespace-nowrap">
                              {new Date(threat.timestamp).toLocaleTimeString()}
                            </div>
                            {index === 0 && (
                              <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-mono bg-green-500/20 text-green-400 border border-green-500/30">
                                NEW
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Threat Statistics */}
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-red-900/20 to-orange-900/20 border border-red-500/30 rounded-lg p-4">
                  <h3 className="text-sm font-mono text-red-400 mb-3 font-semibold">THREAT BREAKDOWN</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs font-mono mb-1">
                        <span className="text-gray-400">Critical</span>
                        <span className="text-red-400 font-bold">
                          {realtimeThreats.filter(t => t.severity === 'critical').length}
                        </span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-red-600 to-red-500 h-2 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${(realtimeThreats.filter(t => t.severity === 'critical').length / Math.max(realtimeThreats.length, 1)) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs font-mono mb-1">
                        <span className="text-gray-400">High</span>
                        <span className="text-orange-400 font-bold">
                          {realtimeThreats.filter(t => t.severity === 'high').length}
                        </span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-orange-600 to-orange-500 h-2 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${(realtimeThreats.filter(t => t.severity === 'high').length / Math.max(realtimeThreats.length, 1)) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs font-mono mb-1">
                        <span className="text-gray-400">Medium</span>
                        <span className="text-yellow-400 font-bold">
                          {realtimeThreats.filter(t => t.severity === 'medium').length}
                        </span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-yellow-600 to-yellow-500 h-2 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${(realtimeThreats.filter(t => t.severity === 'medium').length / Math.max(realtimeThreats.length, 1)) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-lg p-4">
                  <h3 className="text-sm font-mono text-blue-400 mb-3 font-semibold">SYSTEM STATUS</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs font-mono">
                      <span className="text-gray-400">AI Scanner</span>
                      <span className="text-green-400 flex items-center">
                        <span className="w-2 h-2 rounded-full bg-green-500 mr-1 animate-pulse"></span>
                        ACTIVE
                      </span>
                    </div>
                    <div className="flex justify-between text-xs font-mono">
                      <span className="text-gray-400">Email Alerts</span>
                      <span className="text-green-400 flex items-center">
                        <span className="w-2 h-2 rounded-full bg-green-500 mr-1 animate-pulse"></span>
                        ONLINE
                      </span>
                    </div>
                    <div className="flex justify-between text-xs font-mono">
                      <span className="text-gray-400">Geo-Tracking</span>
                      <span className="text-green-400 flex items-center">
                        <span className="w-2 h-2 rounded-full bg-green-500 mr-1 animate-pulse"></span>
                        ENABLED
                      </span>
                    </div>
                    <div className="flex justify-between text-xs font-mono">
                      <span className="text-gray-400">Database</span>
                      <span className="text-green-400 flex items-center">
                        <span className="w-2 h-2 rounded-full bg-green-500 mr-1 animate-pulse"></span>
                        SYNCED
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Detection Analytics - Additional Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-lg shadow-lg p-6 hover:border-purple-400/50 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CpuChipIcon className="h-8 w-8 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-mono text-gray-400 truncate">
                    AI Generated Detected
                  </dt>
                  <dd className="text-lg font-mono text-purple-400">
                    {stats.aiGeneratedDetected}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border border-blue-500/30 rounded-lg shadow-lg p-6 hover:border-blue-400/50 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <EnvelopeIcon className="h-8 w-8 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-mono text-gray-400 truncate">
                    Email Alerts Sent
                  </dt>
                  <dd className="text-lg font-mono text-blue-400">
                    {stats.emailsSent}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-900/20 to-red-900/20 border border-orange-500/30 rounded-lg shadow-lg p-6 hover:border-orange-400/50 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MapPinIcon className="h-8 w-8 text-orange-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-mono text-gray-400 truncate">
                    Flagged Locations
                  </dt>
                  <dd className="text-lg font-mono text-orange-400">
                    {stats.flaggedLocations}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-green-400/50 transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ShieldCheckIcon className="h-8 w-8 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-mono text-gray-400 truncate">
                    Detection Accuracy
                  </dt>
                  <dd className="text-lg font-mono text-green-400">
                    {stats.detectionAccuracy}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* AI Detection Details and Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Recent AI Detections */}
          <div className="bg-black/90 backdrop-blur-md border border-purple-500/30 rounded-lg shadow-lg lg:col-span-2">
            <div className="px-6 py-4 border-b border-purple-500/30">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-mono text-purple-400 flex items-center">
                  <CpuChipIcon className="h-5 w-5 mr-2" />
                  <span className="text-blue-400">[</span>AI-Generated Images Detected<span className="text-blue-400">]</span>
                </h2>
                <span className="text-xs font-mono text-gray-400">Last 24 hours</span>
              </div>
            </div>
            <div className="divide-y divide-purple-500/20">
              {aiDetections.map((detection) => (
                <div key={detection.id} className="px-6 py-4 hover:bg-purple-500/5 transition-colors duration-300">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <PhotoIcon className="h-5 w-5 text-purple-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-mono text-gray-300">{detection.filename}</p>
                        <div className="mt-1 space-y-1">
                          <p className="text-xs text-gray-400 font-mono">
                            <span className="text-red-400">AI Confidence:</span> {detection.aiConfidence}%
                          </p>
                          <p className="text-xs text-gray-400 font-mono">
                            <span className="text-yellow-400">Generator:</span> {detection.generatorType}
                          </p>
                          <p className="text-xs text-gray-400 font-mono">
                            <span className="text-blue-400">📍</span> {detection.location}
                          </p>
                          <p className="text-xs text-gray-400 font-mono">
                            🕐 {formatDate(detection.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                    {detection.notificationSent ? (
                      <span className="px-2 py-1 rounded-full text-xs font-mono bg-green-500/20 text-green-400 border border-green-500/30 flex items-center">
                        <BellIcon className="h-3 w-3 mr-1" />
                        Notified
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-mono bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detection Trends Chart */}
          <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-green-500/30">
              <h2 className="text-lg font-mono text-green-400">
                <span className="text-blue-400">[</span>Weekly Trends<span className="text-blue-400">]</span>
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-3">
                {detectionTrends.map((trend, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-xs font-mono text-gray-400 mb-1">
                      <span>{trend.day}</span>
                      <span>
                        <span className="text-red-400">{trend.aiDetected}</span> / 
                        <span className="text-green-400"> {trend.legitimate}</span>
                      </span>
                    </div>
                    <div className="flex space-x-1 h-8">
                      <div 
                        className="bg-gradient-to-t from-red-500/40 to-red-500/20 border border-red-500/30 rounded"
                        style={{ width: `${(trend.aiDetected / (trend.aiDetected + trend.legitimate)) * 100}%` }}
                        title={`AI: ${trend.aiDetected}`}
                      ></div>
                      <div 
                        className="bg-gradient-to-t from-green-500/40 to-green-500/20 border border-green-500/30 rounded flex-1"
                        title={`Legitimate: ${trend.legitimate}`}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-green-500/20">
                <div className="flex items-center justify-between text-xs font-mono">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500/40 border border-red-500/30 rounded"></div>
                    <span className="text-gray-400">AI Generated</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500/40 border border-green-500/30 rounded"></div>
                    <span className="text-gray-400">Legitimate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Notifications and Flagged Locations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Email Notifications */}
          <div className="bg-black/90 backdrop-blur-md border border-blue-500/30 rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-blue-500/30">
              <h2 className="text-lg font-mono text-blue-400 flex items-center">
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                <span className="text-blue-400">[</span>Email Notifications<span className="text-blue-400">]</span>
              </h2>
            </div>
            <div className="divide-y divide-blue-500/20 max-h-96 overflow-y-auto">
              {emailNotifications.map((email) => (
                <div key={email.id} className="px-6 py-4 hover:bg-blue-500/5 transition-colors duration-300">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <UserGroupIcon className="h-4 w-4 text-blue-400" />
                        <p className="text-sm font-mono text-gray-300">{email.recipient}</p>
                      </div>
                      <p className="text-xs text-gray-400 font-mono mb-1">{email.subject}</p>
                      <div className="flex items-center space-x-3 text-xs font-mono text-gray-500">
                        <span>📍 {email.location}</span>
                        <span>🕐 {formatDate(email.timestamp)}</span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-mono ${
                      email.status === 'delivered' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}>
                      {email.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Flagged Upload Locations */}
          <div className="bg-black/90 backdrop-blur-md border border-orange-500/30 rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-orange-500/30">
              <h2 className="text-lg font-mono text-orange-400 flex items-center">
                <MapPinIcon className="h-5 w-5 mr-2" />
                <span className="text-blue-400">[</span>Flagged Upload Locations<span className="text-blue-400">]</span>
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                {flaggedUploads.map((location, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <MapPinIcon className="h-4 w-4 text-orange-400" />
                        <span className="text-sm font-mono text-gray-300">{location.location}</span>
                      </div>
                      <span className="text-sm font-mono text-orange-400 font-bold">{location.count}</span>
                    </div>
                    <div className="relative h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div 
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
                        style={{ width: `${(location.count / flaggedUploads[0].count) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 font-mono">
                      Coordinates: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-orange-500/20">
                <Link
                  to="/map"
                  className="block w-full text-center py-2 px-4 border border-orange-500/30 rounded-lg text-orange-400 hover:bg-orange-500/10 hover:border-orange-400/50 transition-all duration-300 font-mono text-sm"
                >
                  View on Map →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Documents */}
          <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-green-500/30">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-mono text-green-400">
                  <span className="text-blue-400">[</span>Recent Documents<span className="text-blue-400">]</span>
                </h2>
                <Link 
                  to="/verify" 
                  className="text-blue-400 hover:text-blue-300 text-sm font-mono transition-colors duration-300"
                >
                  Upload New →
                </Link>
              </div>
            </div>
            <div className="divide-y divide-green-500/20">
              {recentDocuments.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-mono text-gray-300">No documents</h3>
                  <p className="mt-1 text-sm text-gray-400 font-mono">
                    Get started by uploading your first document.
                  </p>
                  <div className="mt-6">
                    <Link
                      to="/verify"
                      className="inline-flex items-center px-4 py-2 border border-green-500/50 shadow-sm text-sm font-mono rounded-md text-green-400 bg-green-500/10 hover:bg-green-500/20 transition-all duration-300"
                    >
                      <CloudArrowUpIcon className="-ml-1 mr-2 h-5 w-5" />
                      Upload Document
                    </Link>
                  </div>
                </div>
              ) : (
                recentDocuments.map((doc) => (
                  <div key={doc.id} className="px-6 py-4 hover:bg-green-500/5 transition-colors duration-300">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {getStatusIcon(doc.status)}
                        <div className="ml-3">
                          <p className="text-sm font-mono text-gray-300">
                            {doc.filename}
                          </p>
                          <p className="text-sm text-gray-400 font-mono">
                            {formatDate(doc.created_at)} 
                            {doc.confidence && (
                              <span className="ml-2 text-blue-400">
                                • {doc.confidence}% confidence
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono ${getStatusColor(doc.status)}`}>
                        {doc.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Activities */}
          <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-green-500/30">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-mono text-green-400">
                  <span className="text-blue-400">[</span>Security Activities<span className="text-blue-400">]</span>
                </h2>
                <Link 
                  to="/map" 
                  className="text-blue-400 hover:text-blue-300 text-sm font-mono transition-colors duration-300"
                >
                  View Map →
                </Link>
              </div>
            </div>
            <div className="divide-y divide-green-500/20">
              {activities.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-mono text-gray-300">No activities</h3>
                  <p className="mt-1 text-sm text-gray-400 font-mono">
                    Activity tracking will appear here once you start using the system.
                  </p>
                </div>
              ) : (
                activities.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="px-6 py-4 hover:bg-green-500/5 transition-colors duration-300">
                    <div className="flex items-center">
                      <div className={`h-3 w-3 rounded-full mr-3 ${
                        activity.severity === 'critical' ? 'bg-red-500 animate-pulse' :
                        activity.severity === 'high' ? 'bg-red-400' :
                        activity.severity === 'medium' ? 'bg-yellow-400' :
                        'bg-green-400'
                      }`}></div>
                      <div className="flex-1">
                        <p className="text-sm font-mono text-gray-300">
                          {activity.activity_type}
                        </p>
                        <p className="text-sm text-gray-400 font-mono">
                          <span className="text-blue-400">{activity.location}</span> • {formatDate(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-mono text-green-400 mb-4">
            <span className="text-blue-400">[</span>Quick Actions<span className="text-blue-400">]</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/verify"
              className="flex items-center p-4 border border-green-500/30 rounded-lg hover:bg-green-500/10 hover:border-green-400/50 transition-all duration-300"
            >
              <CloudArrowUpIcon className="h-8 w-8 text-blue-400" />
              <div className="ml-3">
                <p className="text-sm font-mono text-gray-300">Upload Document</p>
                <p className="text-sm text-gray-400 font-mono">Verify new document</p>
              </div>
            </Link>

            <Link
              to="/map"
              className="flex items-center p-4 border border-green-500/30 rounded-lg hover:bg-green-500/10 hover:border-green-400/50 transition-all duration-300"
            >
              <MapPinIcon className="h-8 w-8 text-green-400" />
              <div className="ml-3">
                <p className="text-sm font-mono text-gray-300">View Map</p>
                <p className="text-sm text-gray-400 font-mono">Geographic analysis</p>
              </div>
            </Link>

            <button
              onClick={() => window.location.reload()}
              className="flex items-center p-4 border border-green-500/30 rounded-lg hover:bg-green-500/10 hover:border-green-400/50 transition-all duration-300"
            >
              <ChartBarIcon className="h-8 w-8 text-purple-400" />
              <div className="ml-3">
                <p className="text-sm font-mono text-gray-300">Refresh Data</p>
                <p className="text-sm text-gray-400 font-mono">Update statistics</p>
              </div>
            </button>

            <Link
              to="/profile"
              className="flex items-center p-4 border border-green-500/30 rounded-lg hover:bg-green-500/10 hover:border-green-400/50 transition-all duration-300"
            >
              <EyeIcon className="h-8 w-8 text-orange-400" />
              <div className="ml-3">
                <p className="text-sm font-mono text-gray-300">Profile</p>
                <p className="text-sm text-gray-400 font-mono">Account settings</p>
              </div>
            </Link>
          </div>
        </div>        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/verify"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <CloudArrowUpIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Upload Document</p>
                <p className="text-sm text-gray-500">Verify new document</p>
              </div>
            </Link>

            <Link
              to="/map"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <MapPinIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">View Map</p>
                <p className="text-sm text-gray-500">Geographic analysis</p>
              </div>
            </Link>

            <button
              onClick={() => window.location.reload()}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ChartBarIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Refresh Data</p>
                <p className="text-sm text-gray-500">Update statistics</p>
              </div>
            </button>

            <Link
              to="/profile"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <EyeIcon className="h-8 w-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Profile</p>
                <p className="text-sm text-gray-500">Account settings</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;